import { Money } from "@redotech/money";
import { useHandler } from "@redotech/react-util/hook";
import { Lazy } from "@redotech/react-util/init";
import { getHtml, getReturnType } from "@redotech/redo-model/checkbox";
import { Coverage, PaymentModel } from "@redotech/redo-model/coverage";
import { DEFAULT_IMAGE_URL } from "@redotech/redo-model/default-values";
import {
  Icons,
  InfoModalVersion,
  ToggleFields,
} from "@redotech/redo-model/team";
import { InfoModal } from "@redotech/redo-model/widget";
import GiftIcon from "@redotech/redo-web/arbiter-icon/gift-01.svg";
import LockIcon from "@redotech/redo-web/arbiter-icon/lock-03.svg";
import PlusIcon from "@redotech/redo-web/arbiter-icon/plus.svg";
import ShieldIcon from "@redotech/redo-web/arbiter-icon/shield-tick.svg";
import TrashIcon from "@redotech/redo-web/arbiter-icon/trash-01.svg";
import { CURRENCY_FORMAT } from "@redotech/redo-web/currency";
import LogoIcon from "@redotech/redo-web/icon-old/logo-icon.svg";
import * as classNames from "classnames";
import htmlReactParser from "html-react-parser";
import { lazy, memo, Suspense, useState } from "react";
import * as cardCss from "./cart-card.module.css";
import { TwoLineToggleLabel } from "./toggle";

const RedoModal = lazy(async () => {
  const { RedoModal } = await import("./modal");
  return { default: RedoModal };
});

export const CardImage = memo(function CardImage({
  img_url,
}: {
  img_url: string;
}) {
  return (
    <img
      className={cardCss.imageContainer}
      data-target="card-image"
      src={img_url}
    />
  );
});

export const CartCard = ({
  color,
  coverage,
  html,
  fontSize,
  toggleFields,
  value,
  onChange,
  paymentModel,
  price,
  customCss,
  image_url,
  icon,
  iconColor,
  iconBackgroundColor,
  showIcons = false,
  packageProtectionPlusEnabled,
  packageProtectionEnabled,
  showInfoIcon,
  modalVersion,
  modalCustomizationsV2,
}: {
  color: string;
  coverage: Coverage;
  html?: string | null;
  fontSize?: number;
  toggleFields: ToggleFields;
  value: boolean;
  onChange(value: boolean): void;
  paymentModel: PaymentModel;
  price: Money | undefined;
  customCss?: string;
  image_url: string;
  icon?: Icons;
  iconColor?: string;
  iconBackgroundColor?: string;
  showIcons?: boolean;
  packageProtectionPlusEnabled?: boolean;
  packageProtectionEnabled?: boolean;
  showInfoIcon?: boolean;
  modalVersion?: InfoModalVersion;
  modalCustomizationsV2?: InfoModal;
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const onInfoClick = () => {
    setModalOpen(true);
  };

  const onModalClose = useHandler(() => setModalOpen(false));

  const buttonText = value ? "REMOVE" : "ADD";
  if (image_url === DEFAULT_IMAGE_URL) {
    image_url = "";
  }

  const containerStyles: { color?: string; backgroundColor?: string } = {};
  if (iconColor) {
    containerStyles.color = iconColor;
  }
  if (iconBackgroundColor) {
    containerStyles.backgroundColor = iconBackgroundColor;
  }

  return (
    <>
      {customCss ? <style>{customCss}</style> : null}

      <div className={cardCss.cardContainer} data-target="card-container">
        {image_url && (
          <div className={classNames(cardCss.imageContainer, cardCss.withIcon)}>
            <img
              className={cardCss.imageElement}
              data-target="card-image"
              src={image_url}
            />
            {icon ? (
              <div className={cardCss.iconContainer} style={containerStyles}>
                {icon === "gift" && <GiftIcon className={cardCss.icon} />}
                {icon === "lock" && <LockIcon className={cardCss.icon} />}
                {icon === "shield" && <ShieldIcon className={cardCss.icon} />}
              </div>
            ) : undefined}
          </div>
        )}
        {!image_url && (
          <LogoIcon className={cardCss.redoLogoContainer} data-target="logo" />
        )}

        <div
          className={cardCss.textAndButtonContainer}
          data-target="text-and-buttons-container"
        >
          <TwoLineToggleLabel
            color={color}
            coverage={coverage}
            fontSize={fontSize ?? 14}
            onInfoClick={onInfoClick}
            price={undefined}
            showInfoIcon={showInfoIcon}
            toggleSubtitleText={
              value
                ? toggleFields.positiveSubtext
                : toggleFields.negativeSubtext
            }
            toggleTitleText={toggleFields.titleText}
          >
            {html ? (
              htmlReactParser(getHtml(html, getReturnType(coverage)) ?? "")
            ) : (
              <strong>Free Unlimited Returns</strong>
            )}
          </TwoLineToggleLabel>
          <div className={cardCss.actionItems} data-target="action-items">
            <span data-target="price">
              {price && CURRENCY_FORMAT(price.currency).format(price.amount)}
            </span>
            <button
              className={cardCss.button}
              data-target="card-button"
              onClick={() => {
                onChange(!value);
              }}
              // onTouchStart={() => {
              //   onChange(!value);
              // }}
            >
              {showIcons ? (
                value ? (
                  <TrashIcon className={cardCss.icon} />
                ) : (
                  <PlusIcon className={cardCss.icon} />
                )
              ) : (
                buttonText
              )}
            </button>
          </div>
        </div>
      </div>
      <Suspense>
        <Lazy init={modalOpen}>
          <RedoModal
            enableTopLayerModalApi={
              modalCustomizationsV2?.enableTopLayerModalApi
            }
            modalCustomizationsV2={modalCustomizationsV2}
            onClose={onModalClose}
            open={modalOpen}
            packageProtectionEnabled={packageProtectionEnabled}
            packageProtectionPlusEnabled={packageProtectionPlusEnabled}
            returnsCoverage={coverage}
            version={modalVersion}
          />
        </Lazy>
      </Suspense>
    </>
  );
};
